/**
 * TURN credential renewal for long-lived Janus.js sessions.
 *
 * Janus.js keeps the `iceServers` array given to `new Janus(...)` and passes
 * that same reference to every `RTCPeerConnection` it creates. With
 * `TURN_AUTH_MODE=rest` the backend issues time-limited TURN credentials and
 * renews them on the SIP heartbeat; replacing the array contents in place makes
 * the next call use the fresh credential without recreating the Janus session
 * (which would drop the SIP registration).
 */

export interface TurnIceServersRefreshPayload {
  iceServers?: unknown;
  iceServersExpiresAt?: unknown;
}

export interface TurnIceServersRefreshResult {
  applied: boolean;
  serverCount: number;
  expiresAt: number | null;
}

function isIceServer(value: unknown): value is RTCIceServer {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const { urls } = value as { urls?: unknown };
  if (typeof urls === 'string') {
    return urls.length > 0;
  }
  return Array.isArray(urls) && urls.length > 0 && urls.every((url) => typeof url === 'string');
}

/**
 * Replace `target` contents with refreshed ICE servers from a heartbeat payload.
 *
 * The backend sends the current credentials in both modes, so a
 * `static <-> rest` flip or rollback reaches this tab on the next heartbeat.
 * Returns `applied: false` (and leaves `target` untouched) when the payload has
 * no usable servers (TURN not configured, legacy backend).
 */
export function applyTurnIceServersRefresh(
  target: RTCIceServer[] | null | undefined,
  payload: TurnIceServersRefreshPayload | null | undefined,
): TurnIceServersRefreshResult {
  const expiresAt = typeof payload?.iceServersExpiresAt === 'number' ? payload.iceServersExpiresAt : null;
  const fresh = Array.isArray(payload?.iceServers) ? payload.iceServers.filter(isIceServer) : [];
  if (!target || fresh.length === 0) {
    return { applied: false, serverCount: 0, expiresAt };
  }
  target.splice(0, target.length, ...fresh.map((server) => ({ ...server })));
  return { applied: true, serverCount: fresh.length, expiresAt };
}
